<!--
  - Copyright (c) 2021 brightlamp
  -->

<template>
    <div>
        <v-dialog persistent v-model="dialogLocal" max-width="540px">
            <v-card>
                <v-card-title>
                    <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">Edit Patient Concussion Data</span>
                </v-card-title>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn color="blue darken-1" text @click="closeDialog">Cancel</v-btn>
                    <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
                <v-progress-linear v-if="waiting" :indeterminate="true"/>
                <v-card-text>
                    <v-toolbar dense class="mb-2 indigo white--text" flat>
                        <v-toolbar-title>Concussion Data</v-toolbar-title>
                    </v-toolbar>
                    <v-row dense>
                        <v-col>
                            <v-radio-group v-model="symptomaticType" row>
                                <v-radio label="Asymptomatic" :value="true" color="black"/>
                                <v-radio label="Symptomatic" :value="false" color="black"/>
                            </v-radio-group>
                        </v-col>
                        <v-col>
                            <v-menu
                                v-model="concussionDateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="240px">
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="concussionDateFormatted"
                                        label="Date Last Concussed"
                                        readonly
                                        prepend-icon="mdi-calendar"
                                        v-on="on">
                                    </v-text-field>
                                </template>
                                <v-date-picker color="blue" v-model="concussionDate" no-title :max="todaysDate" @input="concussionDateMenu = false"/>
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-toolbar dense class="mb-4 indigo white--text" flat>
                        <v-toolbar-title>Glasgow Coma Scale - Score: {{ totalScore }}</v-toolbar-title>
                    </v-toolbar>
                    <span class="black--text font-weight-medium mr-2">Eye Opening:</span><span class="black--text font-weight-bold">{{ eyeOpeningFormatted }}</span>
                    <v-slider
                        v-model="eyeOpening"
                        ticks="always"
                        tick-size="8"
                        track-color="black"
                        track-fill-color="green"
                        min="0"
                        max="4"
                        thumb-color="blue"
                        background-color="white">
                        <template v-slot:prepend>
                            <span class="black--text font-weight-bold">0</span>
                        </template>
                        <template v-slot:append>
                            <span class="black--text font-weight-bold">4</span>
                        </template>
                    </v-slider>
                    <span class="black--text font-weight-medium mr-2">Motor Response:</span><span class="black--text font-weight-bold">{{ motorResponseFormatted }}</span>
                    <v-slider
                        v-model="motorResponse"
                        ticks="always"
                        tick-size="8"
                        track-color="black"
                        track-fill-color="green"
                        min="0"
                        max="6"
                        thumb-color="blue"
                        background-color="white">
                        <template v-slot:prepend>
                            <span class="black--text font-weight-bold">0</span>
                        </template>
                        <template v-slot:append>
                            <span class="black--text font-weight-bold">6</span>
                        </template>
                    </v-slider>
                    <span class="black--text font-weight-medium mr-2">Verbal Response:</span><span class="black--text font-weight-bold">{{ verbalResponseFormatted }}</span>
                    <v-slider
                        v-model="verbalResponse"
                        ticks="always"
                        tick-size="8"
                        track-color="black"
                        track-fill-color="green"
                        min="0"
                        max="5"
                        thumb-color="blue"
                        background-color="white">
                        <template v-slot:prepend>
                            <span class="black--text font-weight-bold">0</span>
                        </template>
                        <template v-slot:append>
                            <span class="black--text font-weight-bold">5</span>
                        </template>
                    </v-slider>
                </v-card-text>
            </v-card>
        </v-dialog>
        <ConfirmationDialog v-if="confirmLoseChanges" :params="confirmLoseChangesParams" @confirmation-dialog-response="confirmLoseChangesHandler"></ConfirmationDialog>
    </div>
</template>

<script>
    import {mapActions, mapMutations, mapState} from 'vuex';
    import ConfirmationDialog from '@/components/confirmation-dialog';
    import { DateTime } from "luxon"

    export default {
        name: 'test-concussion-edit-data',
        components:
        {
            ConfirmationDialog
        },
        data: function ()
        {
            return {
                dialogLocal: false,
                concussionDate: null,
                concussionDateFormatted: null,
                concussionDateMenu: false,
                eyeOpening: 0,
                motorResponse: 0,
                verbalResponse: 0,
                eyeOpeningToText: ['Not Answered', 'None', 'To Pain', 'To Speech', 'Spontaneous'],
                motorResponseToText: ['Not Answered', 'None', 'Extension', 'Flexor Response', 'Withdrawal', 'Localizes Pain', 'Obeys Commands'],
                verbalResponseToText: ['Not Answered', 'None', 'Incomprehensible', 'Inappropriate', 'Confused', 'Oriented'],
                confirmLoseChanges: false,
                confirmLoseChangesParams:
                {
                    title: 'About to Lose Changes',
                    text: 'You have unsaved patient concussion updates. Lose changes?',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No'
                },
                updateConcussionDataLocally: false
            }
        },
        props:
        {
            dialog: Boolean(false),
            mrn: String(''),
            resultData: {}
        },
        computed:
        {
            ...mapState('tests',
            {
                concussionDataToMutate: state => state.concussionDataToMutate
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            symptomaticType:
            {
                get: function ()
                {
                    return this.concussionDataToMutate ? this.concussionDataToMutate.asymptomatic : true
                },
                set: function (newValue)
                {
                    this.concussionDataToMutate.asymptomatic = newValue
                }
            },
            todaysDate()
            {
                return new Date().toISOString().split('T')[0]
            },
            eyeOpeningFormatted()
            {
                return this.eyeOpeningToText[this.eyeOpening]
            },
            motorResponseFormatted()
            {
                return this.motorResponseToText[this.motorResponse]
            },
            verbalResponseFormatted()
            {
                return this.verbalResponseToText[this.verbalResponse]
            },
            totalScore()
            {
                if (!this.concussionDataToMutate)
                {
                    return 0
                }
                this.concussionDataToMutate.glasgowComaScale = this.eyeOpening + this.motorResponse + this.verbalResponse;
                return this.concussionDataToMutate.glasgowComaScale
            }
        },
        watch:
        {
            dialogLocal(val)
            {
                // If the dialog is being closed
                if (!val)
                {
                    this.HIDE_SNACKBAR();
                    this.$emit('dialog-close', this.updateConcussionDataLocally)
                }
                else // Else the dialog is being opened
                {
                    this.$logEvent(this.$firebaseAnalytics, 'screen_view',
                    {
                        app_name: 'Web Portal',
                        screen_name: `Reflex Web Portal - ${this.$options.name}`,
                        app_version: process.env.VUE_APP_VERSION
                    });
                    this.dialogLocal = val;
                    // Track the original data
                    this.UPDATE_MUTATED_CONCUSSION_DATA(this.resultData.concussionData);
                    this.concussionDate = null;
                    if (this.concussionDataToMutate.concussionDate)
                    {
                        this.concussionDateFormatted = this.formatDate(this.concussionDataToMutate.concussionDate);
                        const dt = DateTime.fromISO(this.concussionDataToMutate.concussionDate, {zone: 'GMT'})
                        this.concussionDate = `${dt.year}-${dt.month.toString().padStart(2, '0')}-${dt.day.toString().padStart(2, '0')}`
                    }
                }
            },
            dialog(val)
            {
                this.dialogLocal = val
            },
            concussionDate(val)
            {
                this.concussionDateFormatted = this.formatDate(val)
            }
        },
        methods:
        {
            ...mapActions('tests', ['UPDATE_CONCUSSION_DATA']),
            ...mapMutations('tests', ['UPDATE_MUTATED_CONCUSSION_DATA']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_WARN_DELAY', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            closeDialog()
            {
                if (JSON.stringify(this.resultData.concussionData) !== JSON.stringify(this.concussionDataToMutate))
                {
                    this.confirmLoseChanges = true;
                    return
                }
                this.dialogLocal = false
            },
            confirmLoseChangesHandler(payload)
            {
                this.confirmLoseChanges = false;
                // If the changes should be lost
                if (payload.response)
                {
                    this.dialogLocal = false
                }
            },
            async save()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.UPDATE_CONCUSSION_DATA({mrn: this.mrn, visitId: this.resultData.visitId, data: this.concussionDataToMutate});
                    this.updateConcussionDataLocally = true;
                    this.dialogLocal = false
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.WAITING(false)
            },
            formatDate(date)
            {
                if (!date)
                {
                    return null
                }
                // Account for date with . as separators
                if (date.split('T')[0].includes('.'))
                {
                    return date
                }
                const theDate = new Date(date);
                this.concussionDataToMutate.concussionDate = theDate.toISOString();
                return theDate.toLocaleDateString(navigator.languages[0], {timeZone: 'UTC'})
            }
        }
    }
</script>

<style scoped>
</style>